import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { CheckCircle2, CreditCard } from "lucide-react";
import { SiStripe } from "react-icons/si";

import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import {
    useRevokeStripeConnect,
    useStartStripeConnect,
    useStripeConnectStatus,
} from "../../application/useStripeConnect";
import { StripeConnectStatus } from "../../domain/types";

interface StripeConnectPanelProps {
    status: StripeConnectStatus | undefined;
    isLoading: boolean;
    onConnect: () => void;
    connecting: boolean;
    onRevoke: () => void;
    revoking: boolean;
    /** Replaces the default description, e.g. to say whose account this is. */
    description?: string;
}

/**
 * Connecting a Stripe account, whoever owns it.
 *
 * The therapist's own settings and a centre's administration both use this,
 * so the two flows look and read the same; each passes in its own status and
 * actions.
 */
export const StripeConnectPanel: React.FC<StripeConnectPanelProps> = ({
    status,
    isLoading,
    onConnect,
    connecting,
    onRevoke,
    revoking,
    description,
}) => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showConfirm, setShowConfirm] = useState(false);
    const [banner, setBanner] = useState<"connected" | "error" | null>(null);

    // The OAuth callback redirects back here with ?stripe=connected|error.
    // The param is consumed on arrival so a refresh does not replay the
    // message the therapist has already read.
    useEffect(() => {
        const outcome = searchParams.get("stripe");
        if (outcome !== "connected" && outcome !== "error") return;

        setBanner(outcome);
        const next = new URLSearchParams(searchParams);
        next.delete("stripe");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // A deployment with no Stripe credentials gets no section at all, rather
    // than a button that cannot work.
    if (!isLoading && status && status.available === false) return null;

    const connected = Boolean(status?.connected);

    return (
        <section className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
            <div className="mb-1 flex items-center gap-2">
                <CreditCard size={18} className="text-content-tertiary" />
                <h2 className="text-base font-semibold text-content-primary">
                    {t("payments.stripeConnect.title")}
                </h2>
            </div>
            <p className="mb-4 text-sm text-content-tertiary">
                {description ?? t("payments.stripeConnect.description")}
            </p>

            {banner && (
                <p
                    role="status"
                    className={`mb-4 rounded-lg p-3 text-sm ${
                        banner === "connected"
                            ? "bg-green-50 text-green-800 dark:bg-green-500/10 dark:text-green-300"
                            : "bg-red-50 text-red-800 dark:bg-red-500/10 dark:text-red-300"
                    }`}
                >
                    {t(`payments.stripeConnect.${banner}Toast`)}
                </p>
            )}

            {/* Same row as Agendar's Zoom and Google rows, so a therapist who
                has connected one recognises the other. */}
            <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-3 text-sm">
                        {/* Stripe's own mark, drawn unaltered and in the brand
                            colour — the same treatment Agendar gives Zoom, and
                            what Stripe's brand guidelines permit for
                            identifying an integration with them. */}
                        <SiStripe
                            aria-hidden="true"
                            focusable="false"
                            size={28}
                            color="#635BFF"
                        />
                        {connected
                            ? t("payments.stripeConnect.connected", {
                                  accountName:
                                      status?.account_display_name ??
                                      t("payments.stripeConnect.unnamedAccount"),
                              })
                            : t("payments.stripeConnect.rowLabel")}
                    </span>

                    {isLoading ? (
                        <span className="text-sm text-gray-500">
                            {t("common.loading")}
                        </span>
                    ) : connected ? (
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-600">
                                <CheckCircle2 size={14} />
                                {t("payments.stripeConnect.connectedBadge")}
                            </span>
                            <button
                                type="button"
                                onClick={() => setShowConfirm(true)}
                                disabled={revoking}
                                className="text-sm text-red-500 disabled:opacity-50"
                            >
                                {t("payments.stripeConnect.disconnect")}
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            disabled={connecting}
                            onClick={onConnect}
                            className="rounded-lg border border-brand-500 px-3 py-1.5 text-sm font-medium text-brand-600 disabled:opacity-50 dark:text-brand-400"
                        >
                            {connecting
                                ? t("payments.stripeConnect.connecting")
                                : t("payments.stripeConnect.connect")}
                        </button>
                    )}
                </div>
            </div>

            <ConfirmDialog
                isOpen={showConfirm}
                title={t("payments.stripeConnect.disconnect")}
                // Says what breaks, not just "are you sure": links already sent
                // to patients stop working.
                message={t("payments.stripeConnect.disconnectConfirm")}
                confirmLabel={t("payments.stripeConnect.disconnect")}
                cancelLabel={t("common.cancel")}
                destructive
                isBusy={revoking}
                onCancel={() => setShowConfirm(false)}
                onConfirm={() => {
                    onRevoke();
                    setShowConfirm(false);
                }}
            />
        </section>
    );
};

/** The therapist's own Stripe account. */
const StripeConnectCard: React.FC = () => {
    const { data: status, isLoading } = useStripeConnectStatus();
    const startConnect = useStartStripeConnect();
    const revoke = useRevokeStripeConnect();

    return (
        <StripeConnectPanel
            status={status}
            isLoading={isLoading}
            onConnect={() => startConnect.mutate()}
            connecting={startConnect.isPending}
            onRevoke={() => revoke.mutate()}
            revoking={revoke.isPending}
        />
    );
};

export default StripeConnectCard;
